import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SolutionCard from './components/SolutionCard';
import FilterPanel from './components/FilterPanel';
import ComparisonPanel from './components/ComparisonPanel';
import DemoModal from './components/DemoModal';
import RecommendationEngine from './components/RecommendationEngine';
import SearchBar from './components/SearchBar';
import { Solution, FilterOptions, RecommendationInput } from './types';

const SolutionsGallery = () => {
  const [solutions] = useState<Solution[]>([
  {
    id: "retail-footfall",
    title: "Retail Footfall Analytics",
    category: "retail",
    description: "Advanced customer tracking and behavior analysis system that transforms store cameras into powerful business intelligence tools. Track customer journeys, dwell times, and conversion patterns with precision.",
    features: [
    "Real-time visitor counting",
    "Heat map generation",
    "Dwell time analysis",
    "Conversion tracking",
    "Queue management",
    "Staff optimization",
    "Zone analytics",
    "Customer journey mapping"],

    benefits: [
    "Increase conversion rates by 15-25%",
    "Optimize staff allocation and reduce costs",
    "Improve customer experience through data insights",
    "Identify high-performing store areas",
    "Reduce queue times by 30%"],

    image: "https://images.unsplash.com/photo-1711864768464-98c979cb920c",
    alt: "Modern retail store interior with customers shopping and digital analytics overlay showing footfall patterns and heat maps",
    demoAvailable: true,
    accuracy: 96,
    deploymentTime: "2-3 weeks",
    integrations: ["POS Systems", "CRM", "ERP", "Cloud Storage"],
    useCases: ["Retail stores", "Shopping malls", "Supermarkets", "Boutiques"],
    technicalSpecs: {
      algorithm: "Deep Learning CNN",
      processingSpeed: "30 FPS",
      accuracy: "96% in standard conditions",
      scalability: "Up to 100 cameras per node"
    },
    pricing: {
      starter: "$299/month",
      professional: "$799/month",
      enterprise: "Custom"
    },
    compliance: ["GDPR", "CCPA", "ISO 27001"],
    caseStudies: 12
  },
  {
    id: "traffic-atcc",
    title: "ATCC Traffic Management",
    category: "traffic",
    description: "Comprehensive traffic control and monitoring system designed for smart cities. Real-time vehicle detection, classification, and flow optimization with advanced AI algorithms.",
    features: [
    "Vehicle detection & classification",
    "Traffic flow analysis",
    "Incident detection",
    "License plate recognition",
    "Speed monitoring",
    "Signal optimization",
    "Congestion prediction",
    "Emergency vehicle priority"],

    benefits: [
    "Reduce traffic congestion by 40%",
    "Improve emergency response times",
    "Decrease accident rates through early detection",
    "Optimize signal timing automatically",
    "Generate comprehensive traffic reports"],

    image: "https://images.unsplash.com/photo-1677389749026-9399b9c0295d",
    alt: "Busy urban intersection with multiple lanes of traffic and digital overlay showing vehicle tracking and traffic flow analysis",
    demoAvailable: true,
    accuracy: 98,
    deploymentTime: "4-6 weeks",
    integrations: ["Traffic Signals", "IoT Sensors", "City Management Systems", "Emergency Services"],
    useCases: ["Smart cities", "Highway monitoring", "Toll systems", "Parking management"],
    technicalSpecs: {
      algorithm: "YOLO v8 + Tracking",
      processingSpeed: "60 FPS",
      accuracy: "98% vehicle detection",
      scalability: "City-wide deployment"
    },
    pricing: {
      starter: "$1,499/month",
      professional: "$3,999/month",
      enterprise: "Custom"
    },
    compliance: ["ISO 39001", "GDPR", "Local Traffic Regulations"],
    caseStudies: 8
  },
  {
    id: "security-threat",
    title: "Security Threat Detection",
    category: "security",
    description: "AI-powered security system that identifies potential threats, unauthorized access, and suspicious behavior in real-time. Proactive security monitoring with instant alerts.",
    features: [
    "Intrusion detection",
    "Perimeter monitoring",
    "Facial recognition",
    "Suspicious behavior analysis",
    "Weapon detection",
    "Crowd monitoring",
    "Access control integration",
    "Real-time alerts"],

    benefits: [
    "Prevent security incidents before they occur",
    "Reduce false alarms by 80%",
    "24/7 automated monitoring",
    "Instant threat notifications",
    "Comprehensive audit trails"],

    image: "https://images.unsplash.com/photo-1573717871496-bc57b6ae4963",
    alt: "Security control room with multiple monitors displaying surveillance feeds and AI-powered threat detection analytics",
    demoAvailable: true,
    accuracy: 94,
    deploymentTime: "3-4 weeks",
    integrations: ["Access Control", "Alarm Systems", "Security Management", "Mobile Apps"],
    useCases: ["Corporate offices", "Industrial facilities", "Government buildings", "Critical infrastructure"],
    technicalSpecs: {
      algorithm: "Multi-model AI ensemble",
      processingSpeed: "25 FPS",
      accuracy: "94% threat detection",
      scalability: "Enterprise-grade"
    },
    pricing: {
      starter: "$599/month",
      professional: "$1,499/month",
      enterprise: "Custom"
    },
    compliance: ["ISO 27001", "GDPR", "HIPAA", "SOC 2"],
    caseStudies: 15
  },
  {
    id: "business-intelligence",
    title: "Business Intelligence Suite",
    category: "business-intelligence",
    description: "Transform video data into actionable business insights. Comprehensive analytics platform that combines multiple AI models to provide deep operational intelligence.",
    features: [
    "Multi-location analytics",
    "Custom KPI tracking",
    "Predictive analytics",
    "Automated reporting",
    "Dashboard customization",
    "Data visualization",
    "Trend analysis",
    "ROI measurement"],

    benefits: [
    "Make data-driven decisions with confidence",
    "Identify operational inefficiencies",
    "Predict future trends and patterns",
    "Automate reporting processes",
    "Integrate with existing BI tools"],

    image: "https://images.unsplash.com/photo-1642775196125-38a9eb496568",
    alt: "Modern business analytics dashboard displaying multiple charts, graphs, and real-time data visualizations on large screens",
    demoAvailable: true,
    accuracy: 92,
    deploymentTime: "4-8 weeks",
    integrations: ["Power BI", "Tableau", "SAP", "Oracle", "Custom APIs"],
    useCases: ["Enterprise operations", "Multi-location businesses", "Franchise management", "Corporate analytics"],
    technicalSpecs: {
      algorithm: "Hybrid AI/ML models",
      processingSpeed: "Real-time processing",
      accuracy: "92% pattern recognition",
      scalability: "Unlimited locations"
    },
    pricing: {
      starter: "$999/month",
      professional: "$2,499/month",
      enterprise: "Custom"
    },
    compliance: ["GDPR", "CCPA", "ISO 27001", "SOC 2"],
    caseStudies: 10
  },
  {
    id: "retail-queue",
    title: "Queue Management System",
    category: "retail",
    description: "Intelligent queue monitoring and management solution that optimizes customer flow and reduces wait times. Real-time analytics help staff make informed decisions.",
    features: [
    "Queue length detection",
    "Wait time estimation",
    "Staff allocation alerts",
    "Customer flow optimization",
    "Peak hour prediction",
    "Service time analysis",
    "Multi-queue management",
    "Mobile notifications"],

    benefits: [
    "Reduce customer wait times by 40%",
    "Improve customer satisfaction scores",
    "Optimize staff scheduling",
    "Increase throughput during peak hours",
    "Generate actionable insights"],

    image: "https://images.unsplash.com/photo-1624608788853-1082d10865f9",
    alt: "Retail checkout area with multiple queues and digital display showing wait times and queue management analytics",
    demoAvailable: true,
    accuracy: 95,
    deploymentTime: "1-2 weeks",
    integrations: ["POS Systems", "Staff Management", "Customer Apps", "Digital Signage"],
    useCases: ["Retail stores", "Banks", "Airports", "Healthcare facilities"],
    technicalSpecs: {
      algorithm: "Computer Vision + ML",
      processingSpeed: "30 FPS",
      accuracy: "95% queue detection",
      scalability: "Multiple locations"
    },
    pricing: {
      starter: "$199/month",
      professional: "$499/month",
      enterprise: "Custom"
    },
    compliance: ["GDPR", "CCPA", "PCI DSS"],
    caseStudies: 18
  },
  {
    id: "parking-management",
    title: "Smart Parking Analytics",
    category: "traffic",
    description: "Automated parking management system with real-time occupancy tracking, license plate recognition, and payment integration. Maximize parking efficiency and revenue.",
    features: [
    "Space occupancy detection",
    "License plate recognition",
    "Payment integration",
    "Violation detection",
    "Revenue optimization",
    "Mobile app integration",
    "Reservation system",
    "Analytics dashboard"],

    benefits: [
    "Increase parking revenue by 25%",
    "Reduce operational costs",
    "Improve customer experience",
    "Automate enforcement",
    "Real-time availability updates"],

    image: "https://images.unsplash.com/photo-1665243066869-1f27e948de5f",
    alt: "Multi-level parking garage with cars and digital overlay showing available spaces and occupancy analytics",
    demoAvailable: true,
    accuracy: 97,
    deploymentTime: "2-4 weeks",
    integrations: ["Payment Gateways", "Mobile Apps", "Barrier Systems", "City Systems"],
    useCases: ["Shopping centers", "Airports", "Corporate campuses", "Municipal parking"],
    technicalSpecs: {
      algorithm: "Deep Learning + OCR",
      processingSpeed: "Real-time",
      accuracy: "97% LPR accuracy",
      scalability: "Unlimited spaces"
    },
    pricing: {
      starter: "$399/month",
      professional: "$999/month",
      enterprise: "Custom"
    },
    compliance: ["GDPR", "PCI DSS", "Local Regulations"],
    caseStudies: 14
  }]
  );

  const [filteredSolutions, setFilteredSolutions] = useState<Solution[]>(solutions);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [comparisonIds, setComparisonIds] = useState<string[]>([]);
  const [demoSolution, setDemoSolution] = useState<Solution | null>(null);
  const [isDemoOpen, setIsDemoOpen] = useState(false);

  useEffect(() => {
    let filtered = solutions;

    if (searchQuery) {
      filtered = filtered.filter((solution) =>
      solution.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      solution.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      solution.features.some((f) => f.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter((solution) => solution.category === selectedCategory);
    }

    setFilteredSolutions(filtered);
  }, [searchQuery, selectedCategory, solutions]);

  const handleFilterChange = (filters: FilterOptions) => {
    let filtered = solutions;

    if (filters.category !== 'all') {
      filtered = filtered.filter((s) => s.category === filters.category);
    }

    if (filters.accuracy > 0) {
      filtered = filtered.filter((s) => s.accuracy >= filters.accuracy);
    }

    setFilteredSolutions(filtered);
  };

  const handleCompare = (id: string) => {
    if (comparisonIds.includes(id)) {
      setComparisonIds(comparisonIds.filter((cid) => cid !== id));
    } else if (comparisonIds.length < 4) {
      setComparisonIds([...comparisonIds, id]);
    }
  };

  const handleDemo = (id: string) => {
    const solution = solutions.find((s) => s.id === id);
    if (solution) {
      setDemoSolution(solution);
      setIsDemoOpen(true);
    }
  };

  const handleRecommendation = (input: RecommendationInput) => {
    console.log('Recommendation input:', input);
    // Filter solutions based on recommendation input
    let recommended = solutions;

    if (input.industry === 'retail') {
      recommended = recommended.filter((s) => s.category === 'retail');
    } else if (input.industry === 'transportation') {
      recommended = recommended.filter((s) => s.category === 'traffic');
    }

    setFilteredSolutions(recommended);
  };

  const categories = [
  { value: 'all', label: 'All Solutions', icon: 'Layers' },
  { value: 'retail', label: 'Retail Analytics', icon: 'ShoppingCart' },
  { value: 'traffic', label: 'Traffic Management', icon: 'Car' },
  { value: 'security', label: 'Security Solutions', icon: 'Shield' },
  { value: 'business-intelligence', label: 'Business Intelligence', icon: 'BarChart3' }];


  return (
    <>
      <Helmet>
        <title>Solutions Gallery - camlytix AI</title>
        <meta name="description" content="Explore our comprehensive suite of AI-powered video analytics solutions for retail, traffic management, security, and business intelligence." />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                Solutions Gallery
              </h1>
              <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                Discover our comprehensive suite of AI-powered video analytics solutions designed to transform your business operations and drive measurable results
              </p>
            </div>

            <div className="mb-8">
              <RecommendationEngine onRecommend={handleRecommendation} />
            </div>

            <div className="mb-8">
              <SearchBar onSearch={setSearchQuery} />
            </div>

            <div className="flex flex-wrap gap-2 mb-8 justify-center">
              {categories.map((category) =>
              <button
                key={category.value}
                onClick={() => setSelectedCategory(category.value)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                selectedCategory === category.value ?
                'bg-primary text-primary-foreground shadow-brand' :
                'bg-card text-text-secondary hover:bg-muted border border-border'}`
                }>

                  <Icon name={category.icon as any} size={18} />
                  <span>{category.label}</span>
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-1">
                <FilterPanel
                  onFilterChange={handleFilterChange}
                  onReset={() => setFilteredSolutions(solutions)} />

              </div>

              <div className="lg:col-span-3">
                {filteredSolutions.length === 0 ?
                <div className="text-center py-16">
                    <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No solutions found</h3>
                    <p className="text-text-secondary mb-6">
                      Try adjusting your filters or search query
                    </p>
                    <Button
                    variant="outline"
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('all');
                      setFilteredSolutions(solutions);
                    }}>

                      Clear All Filters
                    </Button>
                  </div> :

                <>
                    <div className="flex items-center justify-between mb-6">
                      <p className="text-text-secondary">
                        Showing <span className="font-semibold text-foreground">{filteredSolutions.length}</span> solutions
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-text-secondary">Sort by:</span>
                        <select className="px-3 py-1 bg-card border border-border rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                          <option>Most Popular</option>
                          <option>Highest Accuracy</option>
                          <option>Fastest Deployment</option>
                          <option>Price: Low to High</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {filteredSolutions.map((solution) =>
                    <SolutionCard
                      key={solution.id}
                      solution={solution}
                      onCompare={handleCompare}
                      onDemo={handleDemo}
                      isComparing={comparisonIds.includes(solution.id)} />

                    )}
                    </div>
                  </>
                }
              </div>
            </div>
          </div>
        </main>

        <ComparisonPanel
          solutions={solutions}
          selectedIds={comparisonIds}
          onRemove={(id) => setComparisonIds(comparisonIds.filter((cid) => cid !== id))}
          onClear={() => setComparisonIds([])} />


        <DemoModal
          solution={demoSolution}
          isOpen={isDemoOpen}
          onClose={() => {
            setIsDemoOpen(false);
            setDemoSolution(null);
          }} />

      </div>
    </>);

};

export default SolutionsGallery;